import type { TipTapComment } from '@/hooks/use-threads';
import type { JSONContent } from '@tiptap/react';

const COMMENT_MARK_TYPES = new Set(['inlineThread']);
const COMMENT_NODE_TYPES = new Set(['blockThread']);

function stripCommentAnnotationsFromNode(node: JSONContent): JSONContent | JSONContent[] {
    if (node.type && COMMENT_NODE_TYPES.has(node.type)) {
        const children = node.content ?? [];
        return children.flatMap(child => {
            const result = stripCommentAnnotationsFromNode(child);
            return Array.isArray(result) ? result : [result];
        });
    }

    const marks = node.marks?.filter(mark => !COMMENT_MARK_TYPES.has(mark.type ?? ''));
    const content = node.content?.flatMap(child => {
        const result = stripCommentAnnotationsFromNode(child);
        return Array.isArray(result) ? result : [result];
    });

    const stripped: JSONContent = { ...node };

    if (marks !== undefined) {
        if (marks.length > 0) {
            stripped.marks = marks;
        } else {
            delete stripped.marks;
        }
    }

    if (content !== undefined) {
        stripped.content = content;
    }

    return stripped;
}

/** Removes comment thread marks and nodes so static TipTap schemas can render the document. */
export function stripCommentAnnotationsFromDoc(doc: JSONContent): JSONContent {
    const result = stripCommentAnnotationsFromNode(doc);
    return Array.isArray(result) ? { type: 'doc', content: result } : result;
}

function extractTextFromTipTapJson(node: Record<string, unknown>): string {
    if (node.type === 'text' && typeof node.text === 'string') {
        return node.text;
    }

    if (!Array.isArray(node.content)) {
        return '';
    }

    return node.content.map(child => extractTextFromTipTapJson(child as Record<string, unknown>)).join('');
}

export function getCommentPlainText(content: TipTapComment['content']): string {
    if (typeof content === 'string') {
        return content;
    }

    if (content && typeof content === 'object') {
        return extractTextFromTipTapJson(content);
    }

    return '';
}

export function getThreadPreview(thread: { comments: TipTapComment[] }): string {
    const firstComment = thread.comments[0];
    if (!firstComment) {
        return 'Empty thread';
    }

    const text = getCommentPlainText(firstComment.content).trim();
    return text || 'Empty thread';
}

export function formatCommentTime(createdAt?: string): string {
    if (!createdAt) {
        return '';
    }

    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
    });
}

export function getReplyCount(thread: { comments: TipTapComment[] }): number {
    return Math.max(0, thread.comments.length - 1);
}

export function isCommentAuthoredByUser(comment: TipTapComment, userId: string | null, userName: string | null): boolean {
    const commentUserId = comment.data?.user?.id;
    if (commentUserId && userId) {
        return commentUserId === userId;
    }

    const commentUserName = comment.data?.user?.name;
    if (commentUserName && userName) {
        return commentUserName === userName;
    }

    return false;
}

export function sortThreadsByDocumentPosition<T extends { id: string }>(threads: T[], threadPositions: Map<string, number>): T[] {
    if (threads.length <= 1) {
        return threads;
    }

    return [...threads].sort((a, b) => {
        const posA = threadPositions.get(a.id);
        const posB = threadPositions.get(b.id);

        if (posA === undefined && posB === undefined) {
            return 0;
        }
        if (posA === undefined) {
            return 1;
        }
        if (posB === undefined) {
            return -1;
        }

        return posA - posB;
    });
}

export function isThreadResolved(thread: { resolvedAt?: string | null }): boolean {
    return thread.resolvedAt != null && thread.resolvedAt !== '';
}
